import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CloudUpload, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../../../../empreenderUI/ui/Drawer';
import { translations } from '../../../../utils/language';

const ModalUploadPreview = (props) => {
  const dispatch = useDispatch();
  const controlMedias = useSelector((state) => state.reducerControlMedias);
  const reducerControlVariants = useSelector((state) => state.reducerControlVariants);
  const language = useSelector((state) => state.reducerLanguage);
  const [dragging, setDragging] = useState(false);
  const [dataFiles, setDataFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [dataFilesCount, setDataFilesCount] = useState(0);

  const setUploadFile = (ValueFiles) => {
    const allowedTypes = ['application/pdf'];
    const filteredFiles = Array.from(ValueFiles).filter((file) => allowedTypes.includes(file.type));

    if (filteredFiles.length !== ValueFiles.length) {
      toast.error(translations['products-upload-preview-erro-pdf'][language.data]);
    }

    if (filteredFiles.length > 0) {
      setDataFilesCount(dataFilesCount + filteredFiles.length);
      setDataFiles([...dataFiles, ...filteredFiles]);
    }
  };

  const setUploadFileCompleted = (fileCompleted, index) => {
    setDataFilesCount(dataFilesCount - 1);
    const data = [...dataFiles];
    data[index] = null;
    setDataFiles(data);
    setFiles([...files, fileCompleted]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    setUploadFile(e.dataTransfer.files);
  };

  useEffect(() => {
    if (controlMedias.loading == false && controlMedias.data.length > 0) {
      closeModal();
    }
  }, [controlMedias]);

  const closeModal = () => {
    props.setVisible(false);
    setDataFiles([]);
    setFiles([]);
    setDataFilesCount(0);
    dispatch({ type: 'PRODUCT_CONTROL_MEDIA_RESET' });
    props.close?.();
    dispatch({ type: 'VARIANTS_CONTROL_RESET' });
  };

  const handleOpenChange = (open) => {
    if (!open) closeModal();
  };

  const subtitle = props.contextLabel || translations['variants-menu-upload-preview-desc'][language.data];

  return (
    <Drawer open={!!props.visible} onOpenChange={handleOpenChange} direction='right'>
      <DrawerContent className='!w-[min(420px,100vw)] !max-w-[min(420px,100vw)] !h-full !max-h-[100dvh] border-l border-gray-200 p-0 flex flex-col overflow-hidden bg-white'>
        <DrawerHeader className='relative shrink-0 space-y-0 bg-white px-6 pt-6 pb-2'>
          <div className='flex items-start justify-between gap-3 pr-10'>
            <div className='min-w-0 space-y-1'>
              <DrawerTitle className='text-xl font-bold tracking-tight text-gray-900'>{translations['variants-menu-upload-preview'][language.data]}</DrawerTitle>
              <DrawerDescription className='text-sm font-medium text-gray-500'>{subtitle}</DrawerDescription>
            </div>
          </div>
          <DrawerClose asChild>
            <button type='button' className='absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200' aria-label={translations['common-close'][language.data]}>
              <XMarkIcon className='size-5' />
            </button>
          </DrawerClose>
        </DrawerHeader>

        <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4 custom-scroll'>
          <button
            type='button'
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${dragging ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'}`}
          >
            <CloudUpload className='size-10 text-gray-400' strokeWidth={1.5} />
            <span className='text-sm font-medium text-gray-700'>{translations['products-upload-click-drag-pdf'][language.data]}</span>
            <span className='max-w-[280px] text-xs text-gray-500'>{translations['variants-menu-upload-preview-desc'][language.data]}</span>
            <input
              ref={fileInputRef}
              onChange={(e) => {
                setUploadFile(e.target.files);
                e.target.value = '';
              }}
              className='hidden'
              id='upload-preview-file'
              type='file'
              accept='.pdf,application/pdf'
              multiple
            />
          </button>

          {dataFilesCount > 0 && (
            <div className='flex flex-col gap-3'>
              <label className='text-xs font-medium text-gray-500'>{translations['products-upload-carregando'][language.data]}</label>
              <div className='flex flex-col gap-2'>
                {dataFiles.map((item, index) => {
                  if (item == null) return null;
                  return <CardLoading key={`${item.name}-${index}`} setUploadFileCompleted={setUploadFileCompleted} item={item} index={index} reducerControlVariants={reducerControlVariants} goSearch={props.goSearch} />;
                })}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className='flex flex-col gap-3 border-t border-gray-100 pt-4'>
              <label className='text-xs font-medium text-gray-500'>{translations['products-upload-carregados'][language.data]}</label>
              <div className='flex flex-col gap-2'>
                {files.map((item, index) => (
                  <CardFile key={`${item.name}-${index}`} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ModalUploadPreview;

const CardLoading = (props) => {
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const language = useSelector((state) => state.reducerLanguage);

  const uploadFileS3 = async () => {
    const data = await getUrlUpload();
    if (data) {
      await uploadFile(data);
    } else {
      console.log('erro uploadFileS3');
    }
  };

  const uploadFile = async (data) => {
    const url = data.url + '/' + data.fields.key;
    const formData = new FormData();
    Object.keys(data.fields).forEach((key) => {
      formData.append(key, data.fields[key]);
    });
    formData.append('file', props.item);
    const options = {
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      },
    };
    await axios
      .post(data.url, formData, options)
      .then(async () => {
        if (props.reducerControlVariants.data.length > 0) {
          for await (const variant of props.reducerControlVariants.data) {
            await linkPreviewMedia(variant, url);
          }
          setCompleted(true);
        } else {
          await linkPreviewMedia(null, url);
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.log('Upload cancelado');
        } else {
          console.log('Erro no upload:', error);
          toast.error(translations['products-upload-preview-erro'][language.data]);
        }
      });
  };

  const getUrlUpload = async () => {
    const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token.token;
    const fileExtension = props.item.name?.split('.').pop();
    const url = `${process.env.REACT_APP_API_URL}/getSignedUrl?extFile=${fileExtension}&mimeType=${props.item.type}&sizeFile=${props.item.size}`;
    const headers = new Headers({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const result = await fetch(url, { method: 'GET', headers })
      .then((response) => response.json())
      .catch((error) => {
        console.error('Erro na busca:', error);
      });
    return result?.data;
  };

  const linkPreviewMedia = async (variant, url) => {
    const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token.token;
    const urlRequest = `${process.env.REACT_APP_API_URL}/upload_media`;
    const headers = new Headers({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const fileExtension = props.item.name?.split('.').pop();
    const dataRequest = {
      limit: 0,
      message: '',
      media_name: props.item.name,
      media_url: url,
      media_extname: fileExtension,
      media_size: props.item.size,
    };

    const result = await fetch(urlRequest, {
      method: 'POST',
      headers,
      body: JSON.stringify(dataRequest),
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error('Erro ao criar mídia:', error);
        return null;
      });

    if (result && result.data) {
      const linkUrl = `${process.env.REACT_APP_API_URL}/link_media_product`;
      const linkHeaders = new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });

      let productId = null;
      let variantId = null;

      if (variant != null) {
        productId = variant.variants.product_id;
        variantId = variant.variants.variant_id;
      } else {
        if (props.reducerControlVariants.data.length > 0) {
          productId = props.reducerControlVariants.data[0].variants.product_id;
        } else {
          const urlPath = window.location.pathname;
          const match = urlPath.match(/\/produtos\/(\d+)\/variacoes/);
          if (match) {
            productId = parseInt(match[1]);
          }
        }
        variantId = null;
      }

      const linkData = {
        product_id: productId,
        variant_id: variantId,
        media_id: result.data.id,
        is_preview: true,
      };

      const linkResult = await fetch(linkUrl, {
        method: 'POST',
        headers: linkHeaders,
        body: JSON.stringify(linkData),
      })
        .then((response) => response.json())
        .catch((error) => {
          console.error('Erro ao vincular preview:', error);
          return null;
        });

      if (variant == null) {
        setCompleted(true);
      }
      return linkResult;
    }

    if (variant == null) {
      setCompleted(true);
    }
    return result;
  };

  useEffect(() => {
    if (props?.item) {
      uploadFileS3();
    }
  }, []);

  useEffect(() => {
    if (completed) {
      props.setUploadFileCompleted(props.item, props.index);
      if (props.goSearch) {
        props.goSearch();
      }
    }
  }, [completed]);

  return (
    <div className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5'>
      <span className='inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600'>
        <FileText className='size-4' strokeWidth={1.75} />
      </span>
      <div className='min-w-0 flex-1'>
        <div className='mb-1.5 flex items-center justify-between gap-2'>
          <p className='truncate text-sm font-medium text-gray-900'>{props.item.name}</p>
          <span className='shrink-0 text-xs text-gray-500'>{progress == 100 ? 99 : progress}%</span>
        </div>
        <div className='h-1 w-full overflow-hidden rounded-full bg-gray-100'>
          <div className='h-full rounded-full bg-blue-600 transition-all' style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
};

const CardFile = ({ item }) => {
  return (
    <div className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5'>
      <span className='inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600'>
        <FileText className='size-4' strokeWidth={1.75} />
      </span>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium text-gray-900'>{item.name}</p>
        <p className='truncate text-xs text-gray-500'>PDF</p>
      </div>
    </div>
  );
};
